import React, { ReactNode, useMemo } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { faUpload } from "@fortawesome/pro-solid-svg-icons/faUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ImgCropWithAspect from "antd-img-crop";
import ImgCropWithoutAspect from "antd-img-crop-custom";
import Dropdown from "antd/es/dropdown";
import { ItemType } from "antd/es/menu/hooks/useItems";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { USER_IMAGE_FILE_TYPES, USER_IMAGE_FILE_UPLOAD_LIMIT } from "@app/util/consts";

import styles from "./Image.module.scss";
import InstantUploader from "./InstantUploader";

const messages = defineMessages({
  cropModalTitle: {
    id: "dashboard.header.image.crop.title",
    defaultMessage: "Edit image",
    description: "Image crop/rotate modal title in Dashboard's header",
  },
  removeImage: {
    id: "dashboard.header.image.menu.remove",
    defaultMessage: "Remove image",
    description: "A remove image menu item in Dashboard's header image menu",
  },
  uploadImage: {
    id: "dashboard.header.image.menu.upload",
    defaultMessage: "Upload new image",
    description: "An upload image menu item in Dashboard's header image menu",
  },
});

interface ImageProps {
  alt?: string;
  aspect?: boolean;
  children?: ReactNode;
  className?: string;
  onChange?: (file: File) => void;
  onRemove?: () => void;
  sizeLimit?: number;
  src?: string;
}

const Image: React.FC<ImageProps> = ({
  alt,
  aspect = true,
  children,
  className,
  onChange,
  onRemove,
  sizeLimit = USER_IMAGE_FILE_UPLOAD_LIMIT,
  src,
}) => {
  const intl = useIntl();

  const [uploaderShown, showUploader, hideUploader] = useBoolean();

  const items: ItemType[] = useMemo(
    () => [
      {
        icon: <FontAwesomeIcon icon={faUpload} />,
        key: "upload",
        label: intl.formatMessage(messages.uploadImage),
        onClick: showUploader,
      },
      {
        icon: <FontAwesomeIcon icon={faTrash} />,
        key: "remove",
        label: intl.formatMessage(messages.removeImage),
        onClick: onRemove,
      },
    ],
    [intl, showUploader, onRemove]
  );

  const supportsModification = !!onChange || !!onRemove;

  const ImgCrop = aspect ? ImgCropWithAspect : ImgCropWithoutAspect;

  return (
    <div
      className={cn(styles.container, className, {
        [styles.noImage]: !src,
        [styles.withHoverState]: supportsModification,
      })}
      onClick={src || !supportsModification ? undefined : showUploader}
    >
      {src ? (
        <Dropdown menu={{ items }} trigger={supportsModification ? ["click"] : []}>
          <div className={styles.innerContainer}>
            <img alt={alt} className={styles.image} src={src} />
            <div className={styles.hover}>
              <div className={styles.hoverIcon}>
                <FontAwesomeIcon icon={faPen} />
              </div>
            </div>
          </div>
        </Dropdown>
      ) : (
        children
      )}
      {uploaderShown && (
        <ImgCrop modalTitle={intl.formatMessage(messages.cropModalTitle)} rotationSlider>
          <InstantUploader
            allowedMimeTypes={USER_IMAGE_FILE_TYPES}
            onHide={hideUploader}
            onUpload={onChange}
            sizeLimit={sizeLimit}
          />
        </ImgCrop>
      )}
    </div>
  );
};

export default Image;
